<template>
    <div class="dreamina-page">
        <!-- Image Generator Section -->
        <section ref="generatorSection" class="generator-section">
            <div class="container">
                <div class="generator-header">
                    <h1 class="generator-title">
                        {{ t('dreamina.generatorTitle') }}
                    </h1>
                    <p class="generator-subtitle">
                        {{ t('dreamina.generatorSubtitle') }}
                    </p>
                </div>

                <ImageGenerator model-name="Dreamina 4.0" :default-tab="1" @generate="handleImageGenerate"
                    model-key="DREAMINA_4" />
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('dreamina.coreFeaturesTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('dreamina.coreFeaturesSubtitle') }}
                    </p>
                </div>

                <div class="features-showcase-container">
                    <!-- Feature 1: Multidimensional Control -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('dreamina.feature1Title') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('dreamina.feature1Desc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/dreamina4_image_01.webp"
                                        :alt="t('dreamina.feature1Alt')" loading="lazy" class="feature-image-preview" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 2: Style Transfer -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/dreamina4_image_02.webp"
                                        :alt="t('dreamina.feature2Alt')" loading="lazy" class="feature-image-preview" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('dreamina.feature2Title') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('dreamina.feature2Desc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 3: Detail Optimization -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('dreamina.feature3Title') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('dreamina.feature3Desc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/dreamina4_image_03.webp"
                                        :alt="t('dreamina.feature3Alt')" loading="lazy" class="feature-image-preview" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 4: Sketch Generation -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/dreamina4_image_04.webp"
                                        :alt="t('dreamina.feature4Alt')" loading="lazy" class="feature-image-preview" />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('dreamina.feature4Title') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('dreamina.feature4Desc') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 5: Multi-element Fusion -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('dreamina.feature5Title') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('dreamina.feature5Desc') }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img src="https://static.weryai.com/cms/dreamina4_image_05.webp"
                                        :alt="t('dreamina.feature5Alt')" loading="lazy" class="feature-image-preview" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('dreamina.howToUseTitle') }}
                    </h2>
                    <p class="section-subtitle">
                        {{ t('dreamina.howToUseSubtitle') }}
                    </p>
                </div>

                <ol class="steps-container">
                    <li class="step-item">
                        <div class="step-number">1</div>
                        <h3 class="step-title">
                            {{ t('dreamina.step1Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('dreamina.step1Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">2</div>
                        <h3 class="step-title">
                            {{ t('dreamina.step2Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('dreamina.step2Description') }}
                        </p>
                    </li>

                    <li class="step-item">
                        <div class="step-number">3</div>
                        <h3 class="step-title">
                            {{ t('dreamina.step3Title') }}
                        </h3>
                        <p class="step-description">
                            {{ t('dreamina.step3Description') }}
                        </p>
                    </li>
                </ol>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection :title="t('dreamina.youtubeTitle')" :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts :title="t('dreamina.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('dreamina.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/image/nano-banana" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/3ed8c9e1a1ec3b37133c608c292821cb.webp"
                                :alt="t('dreamina.model1Title')" width="40" height="40" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('dreamina.model1Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/flux" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/a4d5bf7d150f6738f42d3d628ac39869.webp"
                                :alt="t('dreamina.model2Title')" width="40" height="40" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('dreamina.model2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/image/imagen" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/cms/8ec333d0fa59f3ebf4a2373592ae76a6.webp"
                                :alt="t('dreamina.model3Title')" width="40" height="40" loading="lazy" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('dreamina.model3Title') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('dreamina.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <span class="faq-text">{{ t(faq.question) }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ t(faq.answer) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI" class="logo-image" loading="lazy" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('dreamina.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('dreamina.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('dreamina.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('dreamina.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import ImageGenerator from '~/components/ImageGenerator.vue'
import TwitterPosts from '~/components/TwitterPosts.vue'
import YouTubeCarouselSection from '~/components/model/video/YouTubeCarouselSection.vue'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { analyticsEvent } from '~/utils/log-event-func'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({
    pageType: 'image_dreamina',
})

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('dreamina.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// YouTube Videos Data
const youtubeVideos = ref([
    {
        title: 'Dreamina Video 1',
        embedUrl: 'https://www.youtube.com/embed/OEGpY51U5lE',
    },
    {
        title: 'Dreamina Video 2',
        embedUrl: 'https://www.youtube.com/embed/6tz7h-8L1Pc',
    },
    {
        title: 'Dreamina Video 3',
        embedUrl: 'https://www.youtube.com/embed/WO5xzRfVigg',
    },
    {
        title: 'Dreamina Video 4',
        embedUrl: 'https://www.youtube.com/embed/TByFwqroY50',
    },
    {
        title: 'Dreamina Video 5',
        embedUrl: 'https://www.youtube.com/embed/KgOXlC0t1cs',
    },
    {
        title: 'Dreamina Video 6',
        embedUrl: 'https://www.youtube.com/embed/q-foGn7cC9E',
    },
])

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://x.com/dreamina_ai/status/1973517993629065551',
    'https://x.com/iamfakhrealam/status/1988948500349173797',
    'https://x.com/manishkumar_dev/status/1990665218498965812',
    'https://x.com/manishkumar_dev/status/1982039572851876135',
    'https://x.com/iamfakhrealam/status/1989724426250981400',
    'https://x.com/FellMentKE/status/1989350528736985154',
    'https://x.com/AIwithGhotai/status/1991133384396161055',
    'https://x.com/HeyAmit_/status/1991107230037676531',
    'https://x.com/HeyAmit_/status/1989659160779395315',
    'https://x.com/eyishazyer/status/1990379399993253995',
]

// HowTo SEO - 为"如何使用"步骤添加结构化数据
const howToSteps = computed(() => [
    {
        title: t('dreamina.step1Title'),
        description: t('dreamina.step1Description'),
    },
    {
        title: t('dreamina.step2Title'),
        description: t('dreamina.step2Description'),
    },
    {
        title: t('dreamina.step3Title'),
        description: t('dreamina.step3Description'),
    },
])

// 注入 HowTo 结构化数据
useHowToSeo(
    computed(() => t('dreamina.howToUseTitle')),
    howToSteps
)

// FAQ Data
const faqData = ref([
    {
        question: 'dreamina.faq1Question',
        answer: 'dreamina.faq1Answer',
    },
    {
        question: 'dreamina.faq2Question',
        answer: 'dreamina.faq2Answer',
    },
    {
        question: 'dreamina.faq3Question',
        answer: 'dreamina.faq3Answer',
    },
    {
        question: 'dreamina.faq4Question',
        answer: 'dreamina.faq4Answer',
    },
    {
        question: 'dreamina.faq5Question',
        answer: 'dreamina.faq5Answer',
    },
    {
        question: 'dreamina.faq6Question',
        answer: 'dreamina.faq6Answer',
    },
    {
        question: 'dreamina.faq7Question',
        answer: 'dreamina.faq7Answer',
    },
    {
        question: 'dreamina.faq8Question',
        answer: 'dreamina.faq8Answer',
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Image Generation Handler
const handleImageGenerate = (data: {
    prompt: string
    image?: File
    tabType: 'text-to-image' | 'image-to-image'
}) => {
    // console.log('Image generation data:', data)
}

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'dreamina_4_0',
        type: 'image',
    })
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
